import React from 'react';
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownOverlay,
  Heading,
  SelectInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { AdminCreateLoanFormData } from './types';
import { PolicySummary } from './PolicySummary';
import { CACHE_TTL } from 'utils/cache';
import { INTEREST_TYPES, LOAN_CATEGORY_DETAIL_KEY } from './constants';
import { LoanCategory } from 'components/Settings/EmployeeLoansSettings/types';

export const AmountInterest = () => {
  const { setCurrentStep } = useWizardInfo();
  const { formData, setFormData, register } = useFormWithProvider<AdminCreateLoanFormData>();

  const { data: categoryDetail } = useQuery<LoanCategory & { policy_document_url?: string }>({
    queryKey: [LOAN_CATEGORY_DETAIL_KEY, formData.loanCategoryId],
    queryFn: () => api.loans.getLoanCategory(formData.loanCategoryId!),
    enabled: !!formData.loanCategoryId,
    staleTime: CACHE_TTL.MEDIUM,
  });
  const categorySummary = categoryDetail?.policy_summary ?? null;

  const isInterestFree = formData.interestType === '4';

  const canProceed =
    !!formData.principal &&
    Number(formData.principal) > 0 &&
    !!formData.interestType &&
    (isInterestFree ||
      (!!formData.interestRate &&
        Number(formData.interestRate) > 0 &&
        !!formData.perkRate &&
        Number(formData.perkRate) >= 0 &&
        formData.perkRate !== ''));

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          setCurrentStep(2);
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="medium" weight="semibold">
                Amount & Interest
              </Heading>
              <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                Enter the amount and interest details.
              </Text>
            </Box>
            <TextInput
              label="Amount"
              prefix="₹"
              placeholder="Enter Amount"
              type="number"
              {...register('principal')}
            />
            <Checkbox {...register('isAlreadyPaid', 'checkbox')}>
              Amount Already Paid Externally
            </Checkbox>
            <Dropdown selectionType="single">
              <SelectInput
                label="Interest Type"
                placeholder="Choose Interest Type"
                value={formData.interestType || undefined}
                onChange={({ values }) => {
                  const val = values[0] ?? '4';
                  setFormData({
                    ...formData,
                    interestType: val,
                    interestRate: val === '4' ? '' : formData.interestRate,
                    perkRate: val === '4' ? '' : formData.perkRate,
                  });
                }}
              />
              <DropdownOverlay>
                <ActionList>
                  {INTEREST_TYPES.map((t) => (
                    <ActionListItem
                      key={t.value}
                      title={t.label}
                      description={t.description}
                      value={t.value}
                    />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
            {!isInterestFree && formData.interestType && (
              <>
                <TextInput
                  label="Interest Rate (Per Annum)"
                  placeholder="Enter Interest Rate (Per Annum)"
                  suffix="%"
                  type="number"
                  {...register('interestRate')}
                />
                <TextInput
                  label="Perquisite Rate"
                  placeholder="Enter Perquisite Rate"
                  suffix="%"
                  type="number"
                  {...register('perkRate')}
                  helpText="The perquisite rate is the SBI interest rate minus the loan interest rate offered by the organisation."
                />
              </>
            )}
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={() => setCurrentStep(0)}>
            Previous
          </Button>
          <Button type="submit" isDisabled={!canProceed}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <PolicySummary
        policySummary={categorySummary}
        policyDocumentUrl={categoryDetail?.policy_document_url ?? null}
      />
    </Box>
  );
};
